import { useCallback } from 'react';
import { makeUseAxios, UseAxiosResult, UseAxios } from 'axios-hooks';
import axios, { Method, AxiosRequestHeaders, AxiosPromise } from 'axios';

import { useCsrfToken } from '../utils/useCsrfToken';
import { getGlobalConfig } from '../global/ConfigProvider';

interface IRequestParameters {
  url: string;
  headers?: AxiosRequestHeaders;
  method?: Method;
  data?: object;
  params?: object;
}

export const useAxios = <ResponseData = any>(
  requestParameters: IRequestParameters,
  options?: {
    manual?: boolean;
    autoCancel?: boolean;
    useCache?: boolean;
  }
): UseAxiosResult<ResponseData> => {
  const { baseApiUrl } = getGlobalConfig();
  // TODO remove window dependency after split to a separate package
  const rawBaseUrl = baseApiUrl ?? (window.wbConfig || {}).baseUrl;
  const baseURL = typeof rawBaseUrl === 'string' ? rawBaseUrl : '';
  const method = requestParameters.method?.toLowerCase() ?? 'get';
  const defaultOptions = {
    ...options,
    autoCancel: options?.autoCancel ?? method === 'get',
  };
  const useAxiosInstance: UseAxios = makeUseAxios({
    axios: axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        ...requestParameters.headers,
      },
    }),
    defaultOptions,
  });

  const csrfToken: string = useCsrfToken();
  const [{ data, error, loading }, request, cancel] =
    useAxiosInstance<ResponseData>(requestParameters, defaultOptions);

  const customRequest = useCallback(
    (customParameters: IRequestParameters): AxiosPromise => {
      const formData = new FormData();
      formData.append('YII_CSRF_TOKEN', csrfToken);
      if (customParameters?.data) {
        Object.entries(customParameters?.data).forEach(
          ([key, value]: [string, string]) => {
            formData.append(key, value);
          }
        );
      }

      return request({
        ...customParameters,
        data: formData,
      });
    },
    [csrfToken, request]
  );

  return [{ data, error, loading }, customRequest, cancel];
};
